import { useState, useRef, useEffect, useCallback } from "react";

/**
 * useScrollToBottom — 聊天容器滚动控制 Hook
 *
 * 管理消息列表的自动滚动、滚动位置检测、"回到底部"按钮显示。
 * 只关心当前页面的数据和 ref，不感知页面名称。
 *
 * @param {object} options
 * @param {React.RefObject} options.containerRef - 当前页面容器的 ref
 * @param {boolean} options.isLoading - 当前页面是否正在流式加载
 * @param {Array} options.messages - 当前页面的消息列表
 * @param {boolean} options.isMobile - 是否为移动端
 * @returns {{ scrollToBottom: Function, showScrollToBottom: boolean, checkScrollPosition: Function, buttonBottom: string }}
 */
export const useScrollToBottom = ({
    containerRef,
    isLoading,
    messages,
    isMobile = true,
}) => {
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const [buttonBottom, setButtonBottom] = useState("150px");
    const shouldAutoScrollRef = useRef(true);
    const prevLoadingRef = useRef(false);

    /** 滚动到容器底部 */
    const scrollToBottom = useCallback((force = false) => {
        if (!containerRef?.current) return;

        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 120;

        if (force) shouldAutoScrollRef.current = true;

        if (force || shouldAutoScrollRef.current || isAtBottom) {
            if (force) {
                containerRef.current.style.scrollBehavior = "auto";
                containerRef.current.scrollTop = containerRef.current.scrollHeight;
                setTimeout(() => {
                    if (containerRef.current)
                        containerRef.current.style.scrollBehavior = "smooth";
                }, 50);
            } else {
                containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
            shouldAutoScrollRef.current = true;
        }
    }, [containerRef]);

    /** 检测滚动位置，控制"回到底部"按钮显隐 */
    const checkScrollPosition = useCallback(() => {
        const container = containerRef?.current;
        if (!container) return;

        const { scrollHeight, clientHeight, scrollTop } = container;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
        shouldAutoScrollRef.current = isAtBottom;
        setShowScrollToBottom(scrollHeight > clientHeight && !isAtBottom);
    }, [containerRef]);

    // --- 流式输出期间跟随滚动 ---
    useEffect(() => {
        if (isLoading) scrollToBottom(false);

        if (prevLoadingRef.current === true && !isLoading) {
            console.log(">>> [布局修正] 回答结束，执行强制置底以显示潜在的选项栏");
            setTimeout(() => scrollToBottom(false), 150);
        }

        prevLoadingRef.current = isLoading;
        checkScrollPosition();
    }, [messages, isLoading, scrollToBottom, checkScrollPosition]);

    // --- 监听容器尺寸变化，计算按钮位置 ---
    useEffect(() => {
        if (!containerRef?.current) return;

        const updatePosition = () => {
            const bodyHeight = document.body.clientHeight;
            const containerHeight = containerRef.current?.clientHeight || 0;
            const bottomValue = bodyHeight - containerHeight + 10;
            setButtonBottom(
                isMobile
                    ? `${Math.max(bottomValue, 55)}px`
                    : `${Math.max(bottomValue, 150)}px`,
            );
            if (isLoading) scrollToBottom(false);
        };

        const resizeObserver = new ResizeObserver(updatePosition);
        resizeObserver.observe(containerRef.current);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("resize", updatePosition);
            if (containerRef.current) resizeObserver.unobserve(containerRef.current);
        };
    }, [isMobile, isLoading, containerRef, scrollToBottom]);

    return { scrollToBottom, showScrollToBottom, checkScrollPosition, buttonBottom };
};
